import {
  createPresetAnalyticsFilterScope,
  isRollingRangePreset,
  type AnalyticsFilterScope,
  type AnalyticsRangePreset,
} from './AnalyticsFilterScope';
import { AppVersionMultiFilter } from './AppVersionUi';
import { CountryMultiFilter } from './CountryFilterUi';
import { PlatformMultiFilter } from './PlatformUi';

export function AnalyticsFilterBar(props: {
  environmentId: string;
  scope: AnalyticsFilterScope;
  onChange: (scope: AnalyticsFilterScope) => void;
}): React.JSX.Element {
  const scope = props.scope;

  return (
    <article className="surface analytics-filter-bar">
      <div className="range-presets" aria-label="Time range">
        <strong>Time range</strong>
        <div>
          <RangePresetButton value="2h" label="2h" active={scope.preset === '2h'} onSelect={changePreset} />
          <RangePresetButton value="24h" label="24h" active={scope.preset === '24h'} onSelect={changePreset} />
          <RangePresetButton value="7d" label="7 days" active={scope.preset === '7d'} onSelect={changePreset} />
          <RangePresetButton value="30d" label="30 days" active={scope.preset === '30d'} onSelect={changePreset} />
          <RangePresetButton value="90d" label="90 days" active={scope.preset === '90d'} onSelect={changePreset} />
          <RangePresetButton value="365d" label="1 year" active={scope.preset === '365d'} onSelect={changePreset} />
          <RangePresetButton value="custom" label="Custom" active={scope.preset === 'custom'} onSelect={changePreset} />
        </div>
      </div>
      <div className="date-range-inputs">
        {isRollingRangePreset(scope.preset)
          ? <span className="rolling-range-note">Rolling window ending now</span>
          : <>
            <label>From<input type="date" value={scope.from} onChange={changeFrom} /></label>
            <label>Through<input type="date" value={scope.to} onChange={changeTo} /></label>
          </>}
      </div>
      <PlatformMultiFilter selectedCodes={scope.platforms} onChange={changePlatforms} />
      <div className="analytics-dimension-filters">
        <AppVersionMultiFilter
          environmentId={props.environmentId}
          filters={scope}
          selectedVersions={scope.appVersions}
          onChange={changeAppVersions}
        />
        <CountryMultiFilter
          environmentId={props.environmentId}
          filters={scope}
          selectedCountries={scope.countries}
          onChange={changeCountries}
        />
      </div>
    </article>
  );

  function changePreset(preset: AnalyticsRangePreset): void {
    props.onChange(createPresetAnalyticsFilterScope(scope, preset));
  }

  function changeFrom(event: React.ChangeEvent<HTMLInputElement>): void {
    props.onChange({ ...scope, preset: 'custom', from: event.target.value });
  }

  function changeTo(event: React.ChangeEvent<HTMLInputElement>): void {
    props.onChange({ ...scope, preset: 'custom', to: event.target.value });
  }

  function changePlatforms(platforms: number[]): void {
    props.onChange({ ...scope, platforms });
  }

  function changeAppVersions(appVersions: string[]): void {
    props.onChange({ ...scope, appVersions });
  }

  function changeCountries(countries: string[]): void {
    props.onChange({ ...scope, countries });
  }
}

function RangePresetButton(props: {
  value: AnalyticsRangePreset;
  label: string;
  active: boolean;
  onSelect: (value: AnalyticsRangePreset) => void;
}): React.JSX.Element {
  function select(): void {
    props.onSelect(props.value);
  }

  return <button type="button" className={props.active ? 'active' : ''} aria-pressed={props.active} onClick={select}>{props.label}</button>;
}
